import { ResetFiltersButton } from "@/controls/buttons/ResetFiltersButton";
import { Autocomplete, Box, MenuItem, TextField } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import type { Moment } from "moment";
import { ExcelButton } from "@/controls/buttons/ExcelButton";
import {
	BILLET_PROCESS_VARIABLE_NAMES,
	type BilletProcessPickerCrew,
	type BilletProcessPickerProductSize,
	type BilletProcessPickerProductType,
	type BilletProcessPickerVariableName,
	type BilletProcessVariableName,
} from "../models/BilletsProcessData";

interface FilterOption<V> {
	value: V | null;
	label: string;
}

interface SearchableFilterProps<V> {
	label: string;
	value: V | null;
	options: FilterOption<V>[];
	onChange: (value: V | null) => void;
	width: number;
}

const ALL_LABEL = "All";

const SearchableFilter = <V extends string | number>({
	label,
	value,
	options,
	onChange,
	width,
}: SearchableFilterProps<V>) => {
	const all: FilterOption<V> = { value: null, label: ALL_LABEL };
	const list = [all, ...options];
	const selected = list.find((o) => o.value === value) ?? all;

	return (
		<Autocomplete
			options={list}
			value={selected}
			size="small"
			disableClearable
			autoHighlight
			getOptionLabel={(o) => o.label}
			isOptionEqualToValue={(o, current) => o.value === current.value}
			onChange={(_event, o) => onChange(o.value)}
			renderOption={(props, o) => {
				const { key, ...rest } = props as typeof props & { key: string };
				return (
					<li key={String(o.value ?? ALL_LABEL)} {...rest}>
						{o.label}
					</li>
				);
			}}
			sx={{ width }}
			renderInput={(params) => <TextField {...params} label={label} />}
		/>
	);
};

interface Props {
	start: Moment;
	end: Moment;
	onStartChange: (date: Moment | null) => void;
	onEndChange: (date: Moment | null) => void;
	variableName: BilletProcessVariableName | null;
	onVariableNameChange: (v: BilletProcessVariableName | null) => void;
	productTypeId: number | null;
	productSizeId: number | null;
	crewId: string | null;
	onProductTypeChange: (id: number | null) => void;
	onProductSizeChange: (id: number | null) => void;
	onCrewChange: (id: string | null) => void;
	variableNames: BilletProcessPickerVariableName[];
	productTypes: BilletProcessPickerProductType[];
	productSizes: BilletProcessPickerProductSize[];
	crews: BilletProcessPickerCrew[];
	onExportExcel: () => void;
	onReset: () => void;
	canReset: boolean;
}

export const BilletsProcessDataToolbar = ({
	start,
	end,
	onStartChange,
	onEndChange,
	variableName,
	onVariableNameChange,
	productTypeId,
	productSizeId,
	crewId,
	onProductTypeChange,
	onProductSizeChange,
	onCrewChange,
	variableNames,
	productTypes,
	productSizes,
	crews,
	onExportExcel,
	onReset,
	canReset,
}: Props) => {
	const filteredSizes =
		productTypeId == null
			? productSizes
			: productSizes.filter((s) => s.ProductTypeId === productTypeId);

	// Hardcoded fallback — the SP returns empty arrays when the date range
	// has no billets, which leaves every dropdown blank.
	const variableOptions =
		variableNames.length > 0
			? variableNames.map((v) => v.VariableName)
			: (BILLET_PROCESS_VARIABLE_NAMES as readonly string[]);

	return (
		<Box
			sx={{
				display: "flex",
				alignItems: "center",
				gap: 1,
				px: 1.5,
				py: 0.75,
				flexWrap: "wrap",
			}}
		>
			<Box sx={{ minWidth: 150, maxWidth: 170 }}>
				<DatePicker
					label="Start"
					format="MM/DD/YYYY"
					value={start}
					maxDate={end}
					onChange={onStartChange}
					slotProps={{ textField: { size: "small", fullWidth: true } }}
				/>
			</Box>
			<Box sx={{ minWidth: 150, maxWidth: 170 }}>
				<DatePicker
					label="End"
					format="MM/DD/YYYY"
					value={end}
					minDate={start}
					onChange={onEndChange}
					slotProps={{ textField: { size: "small", fullWidth: true } }}
				/>
			</Box>

			<SearchableFilter
				label="Prod Type"
				width={180}
				value={productTypeId}
				options={productTypes.map((pt) => ({ value: pt.Id, label: pt.Name }))}
				onChange={onProductTypeChange}
			/>

			<SearchableFilter
				label="Product"
				width={220}
				value={productSizeId}
				options={filteredSizes.map((ps) => ({ value: ps.Id, label: ps.Name }))}
				onChange={onProductSizeChange}
			/>

			<Box sx={{ width: 110 }}>
				<TextField
					select
					size="small"
					label="Shift"
					fullWidth
					value={crewId ?? ""}
					onChange={(e) => {
						const v = e.target.value;
						onCrewChange(v === "" ? null : v);
					}}
				>
					<MenuItem value="">All</MenuItem>
					{crews.map((c) => (
						<MenuItem key={c.Id} value={c.Id}>
							{c.Id}
						</MenuItem>
					))}
				</TextField>
			</Box>

			<SearchableFilter
				label="Variable Name"
				width={200}
				value={variableName}
				options={variableOptions.map((name) => ({
					value: name as BilletProcessVariableName,
					label: name,
				}))}
				onChange={onVariableNameChange}
			/>

			<ResetFiltersButton onClick={onReset} disabled={!canReset} />

			<Box sx={{ flexGrow: 1, minWidth: 8 }} />

			<Box sx={{ width: 200 }}>
				<ExcelButton handleExportToExcel={onExportExcel} />
			</Box>
		</Box>
	);
};
